import type { Meta, SortDir, SortKey, Task } from '../types';
import { STATUS_LABELS } from '../types';
import { formatDate, relativeDue } from '../lib/format';
import { ScoreCell } from './Score';

interface Props {
  tasks: Task[];
  sort: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  onOpen: (task: Task) => void;
  loading: boolean;
  meta: Meta | null;
}

const COLUMNS: Array<{ key: SortKey; label: string }> = [
  { key: 'priority', label: 'Priority' },
  { key: 'title', label: 'Task' },
  { key: 'system', label: 'System' },
  { key: 'dueDate', label: 'Deadline' },
  { key: 'age', label: 'Age' },
  { key: 'status', label: 'Status' },
];

export function TaskTable({ tasks, sort, dir, onSort, onOpen, loading, meta }: Props) {
  if (!loading && !tasks.length) {
    return (
      <div className="empty">
        <h3>No tasks match these filters</h3>
        <p>Sync a connection, add a task manually, or widen the filters above.</p>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table className="tasks">
        <thead>
          <tr>
            {COLUMNS.map((col) => {
              const isSorted = col.key === sort;
              return (
                <th
                  key={col.key}
                  className={`sortable ${isSorted ? 'sorted' : ''}`}
                  aria-sort={isSorted ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <button onClick={() => onSort(col.key)}>
                    {col.label}
                    <span className="arrow">{isSorted ? (dir === 'asc' ? '▲' : '▼') : '↕'}</span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => {
            const due = relativeDue(task.dueDate);
            const overdue = due.tone === 'overdue' && task.status !== 'done';
            const blockedBy = task.links.filter((l) => l.direction === 'incoming' && l.type === 'blocks');
            const effortLabel = task.effort ? meta?.efforts.find((e) => e.id === task.effort)?.label : null;
            return (
              <tr
                key={task.id}
                onClick={() => onOpen(task)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(task);
                  }
                }}
              >
                <td>
                  <ScoreCell score={task.score} />
                </td>
                <td>
                  <div className="task-title">
                    {task.title}
                    <span className="row-flags">
                      {task.score.pinned && <span className="flag pin">pinned</span>}
                      {task.nudges.length > 0 && <span className="flag nudge">{task.nudges.length} note</span>}
                      {blockedBy.length > 0 && <span className="flag blocked">waiting on {blockedBy.length}</span>}
                      {task.needsTriage && task.status !== 'done' && <span className="flag triage">untriaged</span>}
                    </span>
                  </div>
                  {task.description && <div className="task-desc">{task.description}</div>}
                  <div className="task-meta">
                    {task.projectName && <span>{task.projectName}</span>}
                    {task.itemType && <span>{task.itemType}</span>}
                    {effortLabel && <span>{effortLabel}</span>}
                    {task.commitmentTo && <span>promised to {task.commitmentTo}</span>}
                    {task.externalId && <span className="kv">{task.externalId}</span>}
                    {task.externalUrl && (
                      <a href={task.externalUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                        Open in source ↗
                      </a>
                    )}
                    {task.archived && <span>· archived (no longer returned by the source)</span>}
                  </div>
                </td>
                <td>
                  <span className="pill system-pill">{task.systemLabel}</span>
                </td>
                <td>
                  <div className={`due ${overdue ? 'overdue' : due.tone === 'soon' ? 'soon' : task.dueDate ? '' : 'none'}`}>
                    {formatDate(task.dueDate)}
                    {task.dueDate && <span className="rel">{due.label}</span>}
                  </div>
                </td>
                <td>
                  <span className={`due ${task.ageDays === 0 ? 'none' : ''}`}>
                    {task.ageDays === 0 ? 'Today' : `${task.ageDays}d`}
                  </span>
                </td>
                <td>
                  <span className={`pill ${task.status}`}>
                    <span className="dot" />
                    {STATUS_LABELS[task.status]}
                  </span>
                  {task.sourceStatus && task.sourceStatus !== STATUS_LABELS[task.status] && (
                    <div className="task-meta">
                      <span title="The status as it reads in the source system">{task.sourceStatus}</span>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
